import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { FaShieldAlt } from 'react-icons/fa';
import { FaCheckCircle } from 'react-icons/fa';
import { FaBan } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import TooltipIcon from '../components/ToolTipIcon';

const ShopPage = () => {
  const { shopName } = useParams();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchShopData = async () => {
      try {
        const shopRes = await api.get(`/shops/name/${shopName}`);
        setShop(shopRes.data.shop);
        setProducts(shopRes.data.products);

        if (shopRes.data.shop) {
          const categoriesRes = await api.get(`/shops/${shopRes.data.shop._id}/categories`);
          setCategories(categoriesRes.data);
        }
      } catch (err) {
        setError('Nie znaleziono sklepu lub wystąpił błąd.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchShopData();
  }, [shopName]);

  const renderProducts = (categoryId) => {
    const category = categories.find(c => c._id === categoryId);
    const filteredProducts = products.filter(p => p.category === categoryId);

    return (
      <div>
        <button onClick={() => setSelectedCategory(null)} className="btn-secondary mb-6">
          &larr; Powrót do kategorii
        </button>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <a href={`/product/${product._id}`} key={product._id} className="bg-gray-800 rounded-lg flex flex-col transform hover:-translate-y-2 transition-transform duration-300 shadow-lg hover:shadow-xl overflow-hidden">
              {product.imageUrl && (
                <img src={`${product.imageUrl}`} alt={product.name} className="w-full h-40 object-cover"/>
              )}
              <div className="p-4 flex flex-col flex-grow">
                <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                <p className="text-gray-400 mb-4 flex-grow">{product.description}</p>
                <div className="flex justify-between items-center mt-auto">
                  <span className="text-xl font-semibold" style={{ color: category.color || '#3b82f6' }}>{product.price} PLN</span>
                  <button className="btn-primary" style={{ backgroundColor: category.color || '#3b82f6' }}>Kup teraz</button>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderCategories = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {categories.map((category) => (
        <div
          key={category._id}
          className="bg-gray-800 rounded-lg text-center flex flex-col cursor-pointer transform hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-2xl overflow-hidden"
          onClick={() => setSelectedCategory(category._id)}
        >
          <div
            className="h-32 flex items-center justify-center"
            style={{ backgroundColor: category.color || '#3b82f6' }}
          >
            {category.imageUrl ? (
              <img src={category.imageUrl} alt={category.name} className="w-full h-full object-cover"/>
            ) : (
              <span className="text-4xl font-bold text-white opacity-50">{category.name.charAt(0)}</span>
            )}
          </div>
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-2">{category.name}</h2>
            <p className="text-gray-400">{category.description}</p>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return <div className="text-center mt-8">Ładowanie...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen relative">
      <div className={`container mx-auto p-4 ${shop?.isBlocked ? 'filter blur-xl pointer-events-none' : ''}`}>
        {shop && (
          <>
            <div className="relative rounded-lg mb-8 shadow-lg">
              <div
                className="w-full h-64 rounded-lg bg-cover bg-center"
                style={{
                  backgroundImage: shop.customization?.bannerUrl
                    ? `url(${shop.customization.bannerUrl})`
                    : `linear-gradient(to right, ${shop.customization?.themeColor || '#1e3a8a'}, ${shop.customization?.secondaryColor || '#3b82f6'})`
                }}
              >
              </div>
              <div className="flex items-center p-6 bg-gray-800/50 backdrop-blur-sm rounded-b-lg -mt-20">
                <img
                  src={shop.customization?.logoUrl ? `${shop.customization.logoUrl}` : 'https://placehold.co/80'}
                  alt="Logo sklepu"
                  className="w-20 h-20 rounded-full border-4 border-gray-800 object-cover"
                />
                <div className="ml-6 flex items-center justify-between w-full">
                  <div>
                    <h1 className="text-4xl font-bold text-white shadow-md">
                      {shop.name}
                    </h1>
                    <p className="text-gray-300 text-lg">
                      {shop.serverDescription}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    {shop.isBetaTest && (
                      <TooltipIcon
                        icon={FaShieldAlt}
                        color="text-green-500"
                        text="Właściciel sklepu brał udział w beta testach naszego systemu"
                      />
                    )}

                    {shop.isVerified && (
                      <TooltipIcon
                        icon={FaCheckCircle}
                        color="text-blue-500"
                        text="Sklep zweryfikowany"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {categories.length > 0 ? (
          selectedCategory ? renderProducts(selectedCategory) : renderCategories()
        ) : (
          <div className="text-center py-16">
            <h2 className="text-2xl font-bold text-gray-500">Ten sklep nie ma jeszcze żadnych produktów.</h2>
            <p className="text-gray-600">Wróć później!</p>
          </div>
        )}

      </div>
      {shop?.isBlocked && (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-center p-6 z-50">
      <FaBan className="text-red-500 text-8xl mb-6 animate-pulse" />
      <h2 className="text-3xl font-bold text-red-500 mb-2">Sklep został zablokowany</h2>
      <p className="text-gray-300 mb-6">Nie można wyświetlić zawartości tego sklepu.</p>
      <button
        onClick={() => navigate('/')}
        className="btn-primary bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg text-white font-bold transition-colors"
      >
        Powrót na stronę główną
      </button>
    </div>
  )}
    </div>
  );
};

export default ShopPage;